import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import { getCategory } from "@/mocks/categories";
import { items, getItem, sortedByHeat } from "@/mocks/items";
import { site } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const { id = "" } = useParams();
  const item = getItem(id);
  const cat = item ? getCategory(item.cat) : undefined;
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  const related = item
    ? items.filter((i) => i.id !== item.id && i.cat === item.cat).slice(0, 2)
        .concat(sortedByHeat().filter((i) => i.id !== item.id).slice(0, 1))
    : [];

  useSeo({
    title: item ? `${item.title} | 导航爆料` : "内容详情 | 导航爆料",
    description: item ? item.summary : "内容详情页",
    keywords: `导航爆料,${item ? item.tags.join(",") : ""}`,
    path: `/item/${id}`,
    jsonLd: item
      ? JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: item.title,
          description: item.summary,
          datePublished: "2026-09-08",
          inLanguage: "zh-CN",
          articleSection: cat ? cat.name : "",
          author: { "@type": "Organization", name: site.name },
          publisher: { "@type": "Organization", name: site.name },
          keywords: item.tags.join(","),
        })
      : undefined,
  });

  if (!item || !cat) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
          <i className="ri-file-list-3-line"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">内容不存在或已被移除</h1>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  return (
    <article>
      <Breadcrumb
        crumbs={[
          { label: "首页", to: "/" },
          { label: cat.name, to: `/category/${cat.slug}` },
          { label: "内容详情" },
        ]}
      />

      <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${cat.slug}`}
              className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-200"
            >
              <i className={cat.icon}></i>
              {cat.name}
            </Link>
            {item.tags.map((t) => (
              <span key={t} className="inline-flex items-center rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                # {t}
              </span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-2xl leading-snug font-black text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-primary-700">
                <i className="ri-flashlight-fill"></i>
              </span>
              {site.name}编辑部
            </span>
            <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>发布于 {item.time}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i>{item.reads} 阅读</span>
            <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>约 {item.readMin} 分钟</span>
          </div>
        </header>

        <figure className="mt-6 overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
          <img src={item.cover} alt={item.title} className="aspect-[16/8] w-full object-cover" />
        </figure>

        <div className="mt-8 flex flex-col gap-6 md:flex-row md:gap-10">
          {/* 正文 */}
          <div className="flex-1">
            <p className="border-l-4 border-primary-500 pl-4 text-base leading-8 font-medium text-foreground-800 md:text-lg">
              {item.summary}
            </p>
            <div className="mt-6 space-y-5">
              {item.body.map((p, idx) => (
                <p key={idx} className="text-[15px] leading-8 text-foreground-700 md:text-base">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-background-200 py-4">
              <button
                type="button"
                onClick={() => setLiked((v) => !v)}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                  liked
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                有帮助 {item.comments + (liked ? 1 : 0)}
              </button>
              <button
                type="button"
                onClick={doCopy}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                  copied
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800"
                }`}
              >
                <i className={copied ? "ri-check-line" : "ri-share-line"}></i>
                {copied ? "链接已复制" : "分享链接"}
              </button>
              <a
                href={`mailto:${site.email}?subject=纠错：${item.title}`}
                className="inline-flex items-center gap-2 rounded-full bg-background-100 px-4 py-2 text-sm font-semibold whitespace-nowrap text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
              >
                <i className="ri-feedback-line"></i>
                发现错误
              </a>
            </div>
          </div>

          {/* 侧栏 */}
          <aside className="w-full md:w-72 md:shrink-0">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-background-50"><i className="ri-bookmark-line text-sm"></i></span>
                阅读提示
              </h2>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                <li className="flex gap-2"><i className="ri-check-line text-accent-600"></i>内容基于公开讨论与站内整理</li>
                <li className="flex gap-2"><i className="ri-check-line text-accent-600"></i>涉及数据仅供参考，不构成任何建议</li>
                <li className="flex gap-2"><i className="ri-check-line text-accent-600"></i>争议话题我们并列呈现多方声音</li>
              </ul>
            </div>
            {cat && (
              <div className="mt-5 rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex items-center gap-2 text-xs font-semibold tracking-wider text-primary-600 uppercase">
                  <i className={cat.icon}></i>
                  {cat.name} · 更多
                </span>
                <Link
                  to={`/category/${cat.slug}`}
                  className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-1 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
                >
                  进入栏目
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* 相关推荐 */}
      <section className="mt-12 w-full bg-background-100 py-10">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-stack-line text-primary-500"></i>
            相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <NewsCard key={r.id} item={r} />
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}